"use client"

import * as React from "react"

import { cn } from "@ibada/ui"

/**
 * A multi-step phone form's progress: one segment per step, filled up to the current one. A segment already
 * passed can be tapped to go back to it (going forward still runs each step's own checks).
 */
export function MobileStepProgress({
  steps,
  current,
  onStepPress,
}: {
  steps: { key: string; label: string }[]
  current: number
  onStepPress?: (index: number) => void
}) {
  return (
    <div className='flex gap-1.5 px-1'>
      {steps.map((step, index) => {
        const reached = index <= current
        return (
          <button
            key={step.key}
            type='button'
            onClick={() => (reached && onStepPress ? onStepPress(index) : undefined)}
            disabled={!reached || !onStepPress}
            aria-current={index === current ? "step" : undefined}
            aria-label={step.label}
            className={cn("h-1 flex-1 rounded-full", reached ? "bg-primary" : "bg-muted-foreground/20")}
          />
        )
      })}
    </div>
  )
}

/** A step's title and, cut to two lines until "Plus" is tapped, what it is for. */
export function MobileStepLede({
  title,
  body,
  moreLabel,
  lessLabel,
}: {
  title: string
  body?: string
  moreLabel: string
  lessLabel: string
}) {
  const [open, setOpen] = React.useState(false)
  const [clamped, setClamped] = React.useState(false)
  const bodyRef = React.useRef<HTMLParagraphElement>(null)
  React.useLayoutEffect(() => {
    const node = bodyRef.current
    // Only offer "Plus" when two lines actually cut the text.
    if (node) setClamped(node.scrollHeight > node.clientHeight + 1)
  }, [body])
  return (
    <div className='flex flex-col gap-1 px-1'>
      <h1 className='text-xl leading-tight font-extrabold tracking-tight break-words'>{title}</h1>
      {body ? (
        <>
          <p ref={bodyRef} className={cn("text-[13px] font-semibold text-muted-foreground", !open && "line-clamp-2")}>
            {body}
          </p>
          {clamped || open ? (
            <button type='button' onClick={() => setOpen((value) => !value)} className='self-start text-[13px] font-extrabold text-primary'>
              {open ? lessLabel : moreLabel}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
